// @ts-strict-ignore
import * as React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../reducers/reducers';
import styles from './styles/dashboardStyles';
import { RouteComponentProps, useHistory, useRouteMatch } from 'react-router-dom';
import routes from '../routes';
import { getIdFromAppId, getIdFromOrgId } from '../helpers';
import NewPasswordComponent from './NewPasswordComponent';
import ErrorBoundary from './ErrorBoundary';
import qs from 'query-string';
import { AppRouteComponentMatchParams } from '../typescriptHelpers';
import { selectApp, selectOrgHasAnyApps } from '../selectors/selectors';
import { IsMobileWrapper, useIsMobile } from '../mediaQueriesHelpers';
import { DashboardRoutesProps, DashboardRoutes } from './DashboardRoutes';
import EntityNotFoundComponent from './EntityNotFoundComponent';
import { AppComponentWrapper } from './HOCs/AppComponentWrapper';
import { NavigationSidebar } from './sidebar/NavigationSidebar';
import OrganizationNavigator from './OrganizationNavigator';
import { UserflowOnboarding } from './UserflowOnboarding';
import Layout from 'antd/lib/layout/layout';
import { useNavigationContext } from '../utils/hooks/useNavigationContext';
import { ConfigProvider } from 'antd/lib';
import { useThemeMode } from './styles/themeMode';
import { getAntdThemeTokens } from './styles/globalStyles';
import antdTheme from 'antd/lib/theme';
import { OrgNavigationFiltersProvider } from './sidebar/OrgOverview/OrgNavigationFiltersContext';

const NavigationSidebarWrapper: React.FC = () => {
    const isMobile = useIsMobile();
    const { orgId, appId } = useNavigationContext();

    const { mode } = useThemeMode();
    const isDark = mode !== 'light';

    if (isMobile) {
        return <OrganizationNavigator />;
    }

    return (
        <ConfigProvider
            theme={{
                algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                token: getAntdThemeTokens(isDark ? 'dark' : 'light'),
            }}
        >
            <Layout style={{ minHeight: '100%', maxWidth: '280px' }}>
                <NavigationSidebar orgId={orgId} appId={appId} />
            </Layout>
        </ConfigProvider>
    );
};

const DashboardRoutesContainer: React.FC = () => {
    const hasApps = useSelector((state: RootState) => selectOrgHasAnyApps(state));

    const match = useRouteMatch<AppRouteComponentMatchParams>(routes.app);

    const orgIdParam = match?.params?.orgIdParam;
    const appIdParam = match?.params?.appIdParam;

    const orgId = useSelector((state: RootState) => {
        return orgIdParam ? getIdFromOrgId(orgIdParam, state) : null;
    });
    const appId = useSelector((state: RootState) => {
        return orgIdParam && appIdParam ? getIdFromAppId(orgIdParam, appIdParam, state) : null;
    });

    const app = useSelector((state: RootState) => (appId ? selectApp(state, appId) : null));

    if (appIdParam && !app) {
        return <EntityNotFoundComponent entityType="App" />;
    }

    const props: DashboardRoutesProps = {
        orgId: orgId,
        appId: appId,
        hasApps: hasApps,
        isRestrictedApp: app?.isRestrictedApp ?? false,
    };

    if (orgId && appId) {
        return (
            <AppComponentWrapper orgId={orgId} appId={appId} showLoader showError>
                <UserflowOnboarding key={appId} orgId={orgId} appId={appId} />
                <DashboardRoutes {...props} />
            </AppComponentWrapper>
        );
    } else {
        return <DashboardRoutes {...props} />;
    }
};

const Dashboard: React.FC<RouteComponentProps> = () => {
    const history = useHistory();

    const qParams = qs.parse(history.location.search);
    const signUpAuthSource = qParams?.signUpAuthSource;

    React.useEffect(() => {
        if (signUpAuthSource) {
            // clear query params
            history.replace({ search: null });
        }
    }, [signUpAuthSource]);

    return (
        <ErrorBoundary showRedirectLink={true}>
            <OrgNavigationFiltersProvider>
                <IsMobileWrapper>
                    {(isMobile) => (
                        <div
                            style={{
                                ...styles.dashboardContainerStyle,
                                ...(isMobile ?
                                    {
                                        flexDirection: 'column',
                                        overflowY: 'scroll',
                                    }
                                :   {}),
                            }}
                        >
                            <NewPasswordComponent />
                            <NavigationSidebarWrapper />
                            <ErrorBoundary showRedirectLink={false}>
                                <DashboardRoutesContainer />
                            </ErrorBoundary>
                        </div>
                    )}
                </IsMobileWrapper>
            </OrgNavigationFiltersProvider>
        </ErrorBoundary>
    );
};

export default Dashboard;
